import React, { FC, useState } from 'react'

import { Box, Button, Divider, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import type { TCertificateViewVisibility } from '@repo/types'

import { OwnerProvenanceCard } from 'components/pages/certificates/productPage/OwnerProvenanceCard'
import { usePublicProvenance } from 'hooks/api/usePublicProvenance'
import { useScanLogs } from 'hooks/api/useScanLogs'
import { formatedDate } from 'utils/date'
import { resolvePublicIdentity } from 'utils/parsers'

/** How many rows show before "Show all". */
const PREVIEW_COUNT = 3

interface PublicCertificateSectionsProps {
  certificateId: string
  /**
   * What THIS link exposes. `null` while the owner's views are still loading — render
   * nothing rather than flashing a section that is about to disappear.
   */
  visibility: TCertificateViewVisibility | null
  /** Custom-link slug, so the server applies that view's rules to these reads too. */
  viewSlug?: string | null
}

const SectionHeading: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 2 }}>{children}</Typography>
)

/**
 * VERA-299 — ownership history and scan history on the PUBLIC certificate.
 *
 * The public certificate is a single vertical stack, not the owner's tabbed layout:
 * a visitor arrives by tapping a tag with one question ("is this real, whose is it?"),
 * so everything published is on one scroll rather than behind tabs they have no reason
 * to know about. It also degrades cleanly — a section the owner turned off is simply
 * absent, where a hidden tab would advertise that something was withheld.
 *
 * These sections deliberately do NOT reuse `Provenance` / `ScansTabContent`: those
 * carry the owner's search / filter / sort toolbars, which are furniture for managing
 * a record, not reading one.
 *
 * Client-side gating here is presentation only. The server independently enforces the
 * same rules (empty rows for hidden provenance, nulled prices), so a hand-rolled
 * request cannot get more than the owner published.
 */
export const PublicCertificateSections: FC<PublicCertificateSectionsProps> = ({
  certificateId,
  visibility,
  viewSlug,
}) => {
  const theme = useTheme()
  const [showAllOwners, setShowAllOwners] = useState(false)
  const [showAllScans, setShowAllScans] = useState(false)

  const showProvenance = Boolean(visibility?.show_provenance)
  const showPrices = Boolean(visibility?.show_prices)
  const showScans = Boolean(visibility?.show_activity)
  // Either axis alone is enough to render the timeline: names without amounts, amounts
  // without names, or both. The server has already stripped whichever is withheld.
  const showHistory = showProvenance || showPrices

  const { data: provenance, isLoading: isLoadingProvenance } = usePublicProvenance(
    certificateId,
    viewSlug,
    showHistory
  )
  const { data: scanLogs, isLoading: isLoadingScans } = useScanLogs({
    certificateId,
    limit: 50,
    enabled: showScans,
  } as never)

  if (!visibility || (!showHistory && !showScans)) return null

  const owners = provenance?.data ?? []
  const scans = scanLogs?.data ?? []
  const visibleOwners = showAllOwners ? owners : owners.slice(0, PREVIEW_COUNT)
  const visibleScans = showAllScans ? scans : scans.slice(0, PREVIEW_COUNT)

  /**
   * Who a history row belongs to, on the surface where this matters MOST.
   *
   * This is the anonymous public certificate. The server hands back the whole owner
   * object whenever `show_provenance` is on (`certificates.public.controller.ts` nulls
   * the owner only when it is off), email included — so what renders here is decided
   * entirely on the client, and this component used to pass `item.owner?.email`
   * straight into the card's sub-line. A stranger who opened a shared link read a real
   * address. That is the leak `resolvePublicIdentity` exists to close.
   *
   * The name chain had the second half of the same bug: `first_name`/`last_name` only,
   * so an owner with just a handle fell through to "Previous owner" — anonymous to a
   * reader while their address sat underneath it.
   *
   * "Previous owner" is kept as the last resort. Unlike the authenticated list, a row
   * with no name at all here would otherwise be a blank line in a numbered history, and
   * this copy at least says what the row IS.
   */
  const ownerIdentity = (item: (typeof owners)[number]) => {
    if (item.owner?.name) return { name: item.owner.name, subtext: null }
    const { name, subtext } = resolvePublicIdentity(item.owner)
    return { name: name ?? 'Previous owner', subtext }
  }

  /**
   * The mark for a history row — `logo` for a BRAND, `avatar` for a person.
   *
   * `ProvenanceHistoryPublicItem['owner']` is a union of three shapes and its own
   * docblock says to branch on `owner_type` before reading a field. This read did
   * not: it took `owner.avatar` for every row, and a company row has no `avatar`
   * at all — it carries `logo`. So the origin row, which is ALWAYS the brand and is
   * the one row every certificate has, could never render the brand's mark and
   * always degraded to initials. On a certificate whose brand mark is a signature
   * that is the difference between the maker's own hand and the letters "DA".
   *
   * A store row (VERA-931) has neither field, so it keeps the initials fallback —
   * that is the correct answer there rather than an oversight.
   */
  const ownerMark = (item: (typeof owners)[number]): string | null | undefined => {
    const owner = item.owner as
      | {avatar?: string | null; logo?: string | null}
      | null
      | undefined
    return item.owner_type === 'company' ? owner?.logo : owner?.avatar
  }

  return (
    <Stack sx={{ mt: 4 }}>
      {showHistory && (
        <Box sx={{ mb: showScans ? 4 : 0 }}>
          {/* Named for what it actually contains. With identities withheld this is a
              price history, not an ownership one, and calling it "Ownership history"
              while showing no owners would misdescribe it. */}
          <SectionHeading>
            {showProvenance ? 'Ownership history' : 'Price history'}
          </SectionHeading>

          {isLoadingProvenance ? (
            // Footprint parity: three card-height bars where the cards will land.
            <Stack spacing={2}>
              {[0, 1, 2].map(i => (
                <Skeleton key={i} variant='rounded' height={72} />
              ))}
            </Stack>
          ) : owners.length === 0 ? (
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
              {showProvenance ? 'No previous owners yet.' : 'No recorded sales yet.'}
            </Typography>
          ) : (
            <>
              {visibleOwners.map((item, index) => {
                const identity = ownerIdentity(item)
                return showProvenance ? (
                  <Box key={index} sx={{ mb: 2 }}>
                    <OwnerProvenanceCard
                      name={identity.name}
                      subtext={identity.subtext}
                      avatarUrl={ownerMark(item)}
                      sinceDate={item.ownership_date || ''}
                      // Null when the view hides prices — the server strips it, so the
                      // card has no price row rather than a blank one.
                      sellPrice={item.price ?? undefined}
                      ownerType={item.owner_type === 'company' ? 'company' : 'customer'}
                    />
                  </Box>
                ) : (
                  // Identities withheld: a plain date/amount row rather than an owner
                  // card with the name area empty, which would read as broken.
                  <Stack
                    key={index}
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    spacing={2}
                    sx={{ py: 2, borderBottom: `1px solid ${theme.palette.grey[200]}` }}
                  >
                    <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                      {item.ownership_date
                        ? formatedDate({ date: item.ownership_date })
                        : 'Sale'}
                    </Typography>
                    <Typography sx={{ fontSize: 16 }}>
                      {item.price ?? '—'}
                    </Typography>
                  </Stack>
                )
              })}

              {owners.length > PREVIEW_COUNT && (
                <Button
                  variant='text'
                  onClick={() => setShowAllOwners(v => !v)}
                  sx={{ textTransform: 'none', px: 0 }}
                >
                  {showAllOwners ? 'Show less' : `Show all ${owners.length}`}
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {showProvenance && showScans && <Divider sx={{ mb: 4 }} />}

      {showScans && (
        <Box>
          <SectionHeading>Scan history</SectionHeading>

          {isLoadingScans ? (
            <Stack spacing={2}>
              {[0, 1, 2].map(i => (
                <Skeleton key={i} variant='rounded' height={56} />
              ))}
            </Stack>
          ) : scans.length === 0 ? (
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
              This item has not been scanned yet.
            </Typography>
          ) : (
            <>
              {visibleScans.map(scan => (
                <Stack
                  key={scan.id}
                  direction='row'
                  justifyContent='space-between'
                  alignItems='flex-start'
                  spacing={2}
                  sx={{ py: 2, borderBottom: `1px solid ${theme.palette.grey[200]}` }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16 }}>
                      {scan.metadata?.city || scan.metadata?.country
                        ? [scan.metadata?.city, scan.metadata?.country]
                            .filter(Boolean)
                            .join(', ')
                        : 'Scan location is hidden'}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {scan.user_type === 'anonymous'
                        ? 'Anonymous scan'
                        : scan.user?.first_name
                          ? `${scan.user.first_name} ${scan.user.last_name ?? ''}`.trim()
                          : 'Signed-in scan'}
                    </Typography>
                  </Box>

                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                  >
                    {scan.created_at ? formatedDate({ date: scan.created_at }) : ''}
                  </Typography>
                </Stack>
              ))}

              {scans.length > PREVIEW_COUNT && (
                <Button
                  variant='text'
                  onClick={() => setShowAllScans(v => !v)}
                  sx={{ textTransform: 'none', px: 0, mt: 1 }}
                >
                  {showAllScans ? 'Show less' : `Show all ${scans.length}`}
                </Button>
              )}
            </>
          )}
        </Box>
      )}
    </Stack>
  )
}
